import { Add } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, GreenText, OrderableTableCell, Title } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getNeedsSignedBOLToDoList } from 'controllers/toDoLists/outboundTrips/index'
import { VehicleDto } from 'dtos'
import { NeedsSignedBOLDto, NeedsSignedBOLQueryParams } from 'dtos/toDoLists/outboundTrips'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { formatDate, useDebounce } from 'utils'

export default function NeedsSignedBOLToDoList() {
  const layout = useLayout()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<NeedsSignedBOLQueryParams>({ page: 0, pageSize: 10, order: 'asc', orderBy: 'outboundTripDate' })
  const [toDoList, setToDoList] = useState<NeedsSignedBOLDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: NeedsSignedBOLQueryParams) => {
    layout.setIsLayoutLoading(true)
    getNeedsSignedBOLToDoList(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => {
        layout.setIsLayoutLoading(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Title>Needs Signed BOL</Title>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='outboundTripNumber'>Outbound Trip #</MenuItem>

                    <MenuItem value='outboundTripDate'>Outbound Trip Date</MenuItem>
                  </TextField>
                </Grid>

                {['', 'outboundTripNumber'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {['outboundTripDate'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => setFilterValueFrom(e.target.value)}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => setFilterValueTo(e.target.value)}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={!filterField || (['outboundTripDate'].includes(filterField) ? !filterValueFrom || !filterValueTo : !filterValue)}
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'outboundTripDate') {
                        setQueryParams({
                          ...queryParams,
                          outboundTripDateFrom: filterValueFrom,
                          outboundTripDateTo: filterValueTo
                        })
                      } else {
                        setQueryParams({
                          ...queryParams,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={!Boolean(queryParams.outboundTripNumber || queryParams.outboundTripDateFrom || queryParams.outboundTripDateTo)}
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        outboundTripNumber: undefined,
                        outboundTripDateFrom: undefined,
                        outboundTripDateTo: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.outboundTripNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Outbound Trip # contains "${queryParams.outboundTripNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, outboundTripNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.outboundTripDateFrom && queryParams.outboundTripDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Outbound Trip Date from "${moment(queryParams.outboundTripDateFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.outboundTripDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, outboundTripDateFrom: undefined, outboundTripDateTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <OrderableTableCell<NeedsSignedBOLDto>
                              keyName='outboundTripNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsSignedBOLDto | undefined}
                            >
                              Outbound Trip #
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsSignedBOLDto>
                              keyName='outboundTripDate'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsSignedBOLDto | undefined}
                            >
                              Outbound Trip Date
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsSignedBOLDto>
                              keyName='outboundTransporterCompany'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsSignedBOLDto | undefined}
                            >
                              Outbound Transporter Company
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsSignedBOLDto>
                              keyName='vehiclesInOutboundTrip'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsSignedBOLDto | undefined}
                            >
                              Vehicles In Outbound Trip
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsSignedBOLDto>
                              keyName='internalNotes'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsSignedBOLDto | undefined}
                            >
                              Internal Notes
                            </OrderableTableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {toDoList.map((item, index) => (
                            <TableRow key={item.outboundTripGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell>{item.outboundTripNumber}</TableCell>

                              <TableCell>{formatDate(item.outboundTripDate)}</TableCell>

                              <TableCell>{item.outboundTransporterCompany}</TableCell>

                              <TableCell>{item.vehiclesInOutboundTrip}</TableCell>

                              <TableCell>{item.internalNotes ?? '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={queryParams.pageSize}
                              page={queryParams.page}
                              onPageChange={(_, page) => {
                                setQueryParams({
                                  ...queryParams,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setQueryParams({
                                  ...queryParams,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Needs Signed BOL To-Do List:**

Outbound Trips appear in this list if:

   - The Outbound Trip is locked.
   - A Signed Outbound Bill of Lading (Signed BOL) has not been uploaded.

This list is sorted by Outbound Trip Date, from oldest to newest.

This to-do list ignores the selected Import Branch and always functions as if "All Locations" is selected.

**Removing from To-Do List:**

Users must navigate to the OCR feature and upload a scanned PDF of the Signed Outbound Bill of Lading.

The document must be properly associated with the relevant Outbound Trip in order for it to be removed from this list.

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   Outbound Trip #,
   Outbound Trip Date

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   Outbound Trip #: Displays a free-type filter field.
   Outbound Trip Date: Displays Filter Date From and Filter Date To.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Outbound Trip # Column:**

Displays the unique identifier of the Outbound Trip to which the vehicle is assigned.

**Outbound Trip Date Column:**

Displays the scheduled date of the Outbound Trip.

**Outbound Transporter Company Column:**

Displays the company name for the Outbound Transporter Company.

**Vehicles In Outbound Trip Column:**

Displays the total number of vehicles in the Outbound Trip.

**Internal Notes Column:**

Displays any entered notes internal notes for the Outbound Trip.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
